import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { defaultInvoiceStatusOptions } from '@nxsys/constants/AppConstants';
import { InvoiceStatus, InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { GetInvoiceListParams } from '@nxsys/types/api';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: GetInvoiceListParams) => void;
  invoiceType?: InvoiceTypeEnum;
};

type FormValues = {
  status: string;
  startDate: string;
  endDate: string;
  invoiceNo: string;
  totalPrices: string;
  agencies: Option<string>[];
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
  invoiceType = InvoiceTypeEnum.INVOICE,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();

  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm<FormValues>();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue &&
    Object.keys(watchValue).some(
      (key) => !!watchValue[key as keyof FormValues],
    );

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values: FormValues) => {
    const params: GetInvoiceListParams = {};
    if (values.status === InvoiceStatus.OUTSTANDING)
      params.isOutstanding = true;
    if (values.status === InvoiceStatus.PAID) params.isPaid = true;
    if (values.startDate)
      params.startDate = moment(values.startDate).toISOString();
    if (values.endDate) params.endDate = moment(values.endDate).toISOString();
    if (values.invoiceNo) params.invoiceNo = values.invoiceNo;
    if (values.totalPrices) params.totalPrices = values.totalPrices;
    if (values.agencies)
      params.agencyIds = values.agencies.map((agency) => agency.value);
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // noop - form validation handles error display
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {invoiceType !== InvoiceTypeEnum.CREDIT_NOTE && !isBureau && (
          <Form.Item name="status" label={messages['common.status']}>
            <StyledAppSelect
              options={defaultInvoiceStatusOptions}
              placeholder={messages['form.pleaseSelect']}
              size="large"
              defaultValue={InvoiceStatus.ALL}
            />
          </Form.Item>
        )}
        <div className="flex gap-x-4">
          <Form.Item
            key={'startDate'}
            label={messages['common.from']}
            name={'startDate'}
            className="w-1/2"
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  startDate: date.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
          <Form.Item
            key={'endDate'}
            label={messages['common.to']}
            name={'endDate'}
            layout="vertical"
            className="w-1/2"
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  endDate: date.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </div>
        {isBureau ? (
          <>
            <Form.Item
              name="invoiceNo"
              label={messages['invoices.invoiceNumber']}
            >
              <AppInput placeholder={messages['form.pleaseEnter']} />
            </Form.Item>
            <Form.Item name="totalPrices" label={messages['common.total']}>
              <AppCurrencyInput
                placeholder={messages['form.pleaseEnter']}
              />
            </Form.Item>
          </>
        ) : (
          <Form.Item label="Agency" name="agencies">
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder="Select agency"
              className="w-full max-w-full"
              onChangeValue={(value: any) => {
                form.setFieldsValue({ agencies: value });
              }}
              mode="multiple"
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
